import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { fetchRoles, updateMemberRole } from '@/services/teamService';
import { Role } from '@/types/team';
import { useTeampageTranslations } from '@/hooks/useTeampageTranslations';
import { logError } from '@/utils/errorHandler';

interface SelectRoleModalProps {
  membershipId: string | number;
  currentRoleName: string;
  open: boolean;
  onClose: () => void;
}

const SelectRoleModal: React.FC<SelectRoleModalProps> = ({
  membershipId,
  currentRoleName,
  open,
  onClose,
}) => {
  // Get translations
  const i18n = useTeampageTranslations();

  const { user } = useAuth();
  const orgId = user?.organization_id;
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  // Fetch available roles
  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ['roles', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error(i18n.errors.organizationIdMissing);
      }
      return fetchRoles(orgId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(orgId) && open, // Only run if we have an org ID and modal is open
  });

  // Filter out OrgOwner role (cannot be assigned manually)
  const selectableRoles = roles.filter(role => role.name !== 'OrgOwner');

  // Setup mutation for updating role
  const updateMutation = useMutation({
    mutationFn: ({ membershipId, roleId }: { membershipId: string | number; roleId: number }) => {
      if (!orgId) {
        throw new Error(i18n.errors.organizationIdMissing);
      }
      return updateMemberRole(Number(membershipId), roleId, orgId);
    },
    onSuccess: () => {
      toast.success(i18n.roleChangeModal.roleUpdated);
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      onClose();
    },
    onError: (error: any) => {
      logError(error, {
        component: 'SelectRoleModal',
        action: 'updateMemberRole',
        metadata: {
          membershipId,
          selectedRoleId: selectedRole?.id,
          selectedRoleName: selectedRole?.name,
          currentRoleName,
          orgId,
        },
      });
      toast.error(
        i18n.roleChangeModal.failedToUpdate(error.message || i18n.roleChangeModal.unknownError)
      );
    },
  });

  // Find and select current role on load (respect filter)
  useEffect(() => {
    if (open && selectableRoles.length > 0) {
      const current = selectableRoles.find(role => role.name === currentRoleName);
      setSelectedRole(current || null);
    }
  }, [open, selectableRoles, currentRoleName]);

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role);
  };

  const handleSave = () => {
    if (!selectedRole) {
      toast.error(i18n.roleChangeModal.pleaseSelectRole);
      return;
    }

    if (selectedRole.name === currentRoleName) {
      toast.info(i18n.roleChangeModal.roleUnchanged);
      onClose();
      return;
    }

    if (selectedRole.name === 'OrgOwner') {
      toast.error('Cannot assign Org Owner role');
      return;
    }

    updateMutation.mutate({
      membershipId,
      roleId: selectedRole.id,
    });
  };

  return (
    <Dialog open={open} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{i18n.roleChangeModal.title}</DialogTitle>
          <DialogDescription>{i18n.roleChangeModal.description}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-4">
            {selectableRoles.map(role => (
              <div
                key={role.id}
                className={`cursor-pointer rounded-md border p-4 transition-colors hover:bg-accent/50 ${
                  selectedRole?.id === role.id ? 'border-primary bg-accent' : ''
                }`}
                onClick={() => handleRoleSelect(role)}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`size-4 rounded-full ${selectedRole?.id === role.id ? 'bg-primary' : 'border border-muted-foreground'}`}
                  />
                  <div className="font-medium">
                    {i18n.roles[role.name as keyof typeof i18n.roles]?.name || role.name}
                  </div>
                </div>
                <div className="ml-6 mt-1 text-sm text-muted-foreground">
                  {i18n.roles[role.name as keyof typeof i18n.roles]?.description ||
                    role.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
            {i18n.roleChangeModal.cancel}
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending || !selectedRole}>
            {updateMutation.isPending
              ? i18n.roleChangeModal.saving
              : i18n.roleChangeModal.saveChanges}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelectRoleModal;
